import React, { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";
import { logSupabaseError } from "./utils";
import "./App.css";

export default function AlertsBadge() {
    const [alerts, setAlerts] = useState([]);
    const [showPanel, setShowPanel] = useState(false);
    const [loading, setLoading] = useState(true);

    const intervalRef = useRef(null);

    useEffect(() => {
        fetchAlerts();
        intervalRef.current = setInterval(fetchAlerts, 5 * 60 * 1000);
        return () => clearInterval(intervalRef.current);
    }, []);

    async function fetchAlerts() {
        setLoading(true);
        const allAlerts = [];

        try {
            // ── 1. Low stock alerts ──────────────────────────
            const { data: allProducts, error: productsError } = await supabase
                .from("products")
                .select("id, name, stock_qty, low_stock_threshold, sku_code, shelf_location")
                .eq("is_active", true);
            logSupabaseError("AlertsBadge: fetch products", productsError);

            if (allProducts) {
                allProducts
                    .filter((p) => Number(p.stock_qty) <= Number(p.low_stock_threshold))
                    .forEach((p) => {
                        allAlerts.push({
                            id: p.id + "-low",
                            type: p.stock_qty <= 0 ? "out" : "low",
                            title: p.stock_qty <= 0 ? "Out of Stock" : "Low Stock",
                            product: p.name,
                            sku: p.sku_code,
                            shelf: p.shelf_location,
                            detail: p.stock_qty <= 0
                                ? "No stock on shelf"
                                : `Only ${p.stock_qty} left · Threshold: ${p.low_stock_threshold}`,
                            priority: p.stock_qty <= 0 ? 1 : 2,
                        });
                    });
            }

            // ── 2. Expiry alerts (within 30 days) ────────────
            const today = new Date();
            const in30days = new Date();
            in30days.setDate(today.getDate() + 30);

            const { data: batches, error: batchesError } = await supabase
                .from("product_batches")
                .select("id, batch_number, expiry_date, quantity, products(name, sku_code)")
                .lte("expiry_date", in30days.toISOString().split("T")[0])
                .gte("expiry_date", today.toISOString().split("T")[0])
                .gt("quantity", 0)
                .order("expiry_date");
            logSupabaseError("AlertsBadge: fetch product_batches (expiry)", batchesError);

            if (batches) {
                batches.forEach((b) => {
                    const daysLeft = Math.ceil(
                        (new Date(b.expiry_date) - today) / (1000 * 60 * 60 * 24)
                    );
                    allAlerts.push({
                        id: b.id + "-expiry",
                        type: daysLeft <= 7 ? "expiry-critical" : "expiry-warning",
                        title: daysLeft <= 7 ? "Expiring Soon!" : "Expiring in 30 Days",
                        product: b.products?.name ?? "Unknown",
                        sku: b.products?.sku_code,
                        detail: `Batch ${b.batch_number} · ${b.quantity} units · Expires ${new Date(b.expiry_date).toLocaleDateString("en-US", {
                            month: "short", day: "numeric", year: "numeric"
                        })
                            } (${daysLeft} days)`,
                        priority: daysLeft <= 7 ? 1 : 3,
                        daysLeft,
                    });
                });
            }

        } catch (err) {
            console.error("AlertsBadge error:", err);
        }

        allAlerts.sort((a, b) => a.priority - b.priority);
        setAlerts(allAlerts);
        setLoading(false);
    }

    const criticalCount = alerts.filter((a) =>
        a.type === "out" || a.type === "expiry-critical"
    ).length;
    const totalCount = alerts.length;

    // Don't show badge while loading or if no alerts
    if (loading && totalCount === 0) return null;
    if (!loading && totalCount === 0) return null;

    return (
        <>
            {/* ── Badge Button ── */}
            <button
                className="alerts-badge-btn"
                onClick={() => setShowPanel(true)}
                title={`${totalCount} alert${totalCount !== 1 ? "s" : ""}`}
            >
                <span className="alerts-badge-icon">⚠</span>
                <span className={`alerts-badge-count ${criticalCount > 0 ? "critical" : "warning"}`}>
                    {totalCount}
                </span>
            </button>

            {/* ── Alerts Panel ── */}
            {showPanel && (
                <div className="alerts-overlay" onClick={() => setShowPanel(false)}>
                    <div className="alerts-panel" onClick={(e) => e.stopPropagation()}>

                        {/* Header */}
                        <div className="alerts-header">
                            <div>
                                <h3 className="alerts-title">⚠ Alerts</h3>
                                <p className="alerts-sub">
                                    {criticalCount > 0 && (
                                        <span style={{ color: "var(--red)" }}>
                                            {criticalCount} critical ·{" "}
                                        </span>
                                    )}
                                    {totalCount} total
                                </p>
                            </div>
                            <div style={{ display: "flex", gap: "8px" }}>
                                <button
                                    className="wh-add-item-btn"
                                    onClick={(e) => { e.stopPropagation(); fetchAlerts(); }}
                                >
                                    ↻ Refresh
                                </button>
                            </div>
                            <button
                                className="report-close"
                                onClick={() => setShowPanel(false)}
                            >✕</button>
                        </div>

                        {/* Alert List */}
                        <div className="alerts-list">
                            {alerts.length === 0 ? (
                                <div style={{ padding: "32px", textAlign: "center", color: "var(--text3)" }}>
                                    <p style={{ fontSize: "28px", marginBottom: "8px" }}>✅</p>
                                    <p>All clear! No alerts.</p>
                                </div>
                            ) : (
                                alerts.map((alert) => (
                                    <div key={alert.id} className={`alert-item ${alert.type}`}>
                                        <div className="alert-icon">
                                            {alert.type === "out" ? "❌"
                                                : alert.type === "low" ? "📉"
                                                    : alert.type === "expiry-critical" ? "🚨"
                                                        : "⏰"}
                                        </div>
                                        <div className="alert-content">
                                            <div className="alert-header-row">
                                                <span className="alert-title">{alert.title}</span>
                                                <span className={`alert-badge ${alert.type}`}>
                                                    {alert.type === "out" ? "OUT OF STOCK"
                                                        : alert.type === "low" ? "LOW STOCK"
                                                            : `${alert.daysLeft}d left`}
                                                </span>
                                            </div>
                                            <p className="alert-product">{alert.product}</p>
                                            {alert.sku && (
                                                <p className="alert-sku">{alert.sku}</p>
                                            )}
                                            {alert.shelf && (
                                                <p style={{ fontSize: "11px", color: "var(--purple)", marginTop: "2px" }}>
                                                    📍 {alert.shelf}
                                                </p>
                                            )}
                                            <p className="alert-detail">{alert.detail}</p>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}